import api from './api'
import {
  AvaliacaoRespostaRequest,
  CriarOportunidadeRequest,
  RespostaElegibilidadeReserva,
  RespostaElegibilidadeRequest,
  Oportunidade,
  OperacaoAnteriorCandidata,
  OportunidadeRequest,
  OportunidadeUpdateRequest,
  ConsultorReserva,
  DefesaCreditoRequest,
  RenovarReservaRequest,
  TransferirReservaRequest,
  TransferenciaReserva,
  EncaminhamentoParceiro,
  SolicitarReuniaoRequest,
} from '@/types/oportunidade'
import { AtualizarStatusRequest } from '@/types/statusReserva'
import { HistoricoEtapa } from '@/types/scoreComercial'
import { DocumentoObrigatorio } from '@/utils/docsObrigatorios'
import { PageResponse } from '@/types/comum'

const BASE_URL = '/api/v1/oportunidades'

// Importante: o backend de oportunidades NÃO embrulha respostas em ApiResponse.
// O interceptor de `api` já devolve o body HTTP — basta retornar `response`.
export const oportunidadeService = {
  listar: async (page: number = 0, size: number = 10): Promise<PageResponse<Oportunidade>> => {
    const response = await api.get<PageResponse<Oportunidade>>(
      `${BASE_URL}?page=${page}&size=${size}`
    )
    return response as unknown as PageResponse<Oportunidade>
  },

  /**
   * Busca reservas por prefixo de CNPJ (3+ dígitos) ou trecho da razão social / nome fantasia
   * (2+ letras), no escopo de quem pede — consultor só a própria carteira. Usada pelo seletor
   * de reserva da Nova Reunião (10/10/2026) em vez de carregar as 200 primeiras linhas.
   */
  buscarPorTermo: async (termo: string, size: number = 20): Promise<PageResponse<Oportunidade>> => {
    const params = new URLSearchParams({ busca: termo, page: '0', size: String(size) })
    const response = await api.get<PageResponse<Oportunidade>>(`${BASE_URL}?${params.toString()}`)
    return response as unknown as PageResponse<Oportunidade>
  },

  /**
   * Consultores com reserva no escopo de quem pede, com o nome gravado na reserva — opções do
   * filtro "Consultor" do back-office sem depender da lista de usuários, que só o ADMIN lê.
   */
  listarConsultores: async (): Promise<ConsultorReserva[]> => {
    const response = await api.get<ConsultorReserva[]>(`${BASE_URL}/consultores`)
    return response as unknown as ConsultorReserva[]
  },

  buscar: async (id: string): Promise<Oportunidade> => {
    const response = await api.get<Oportunidade>(`${BASE_URL}/${id}`)
    return response as unknown as Oportunidade
  },

  criar: async (request: OportunidadeRequest): Promise<Oportunidade> => {
    const response = await api.post<Oportunidade>(BASE_URL, request)
    return response as unknown as Oportunidade
  },

  /**
   * Criação pela página Nova oportunidade: mesmo endpoint do `criar`, corpo
   * superconjunto com identidade da empresa (CNPJ/razão social). A reserva nasce em
   * RASCUNHO; o consultor sobe os documentos em seguida e só então envia para
   * aprovação. O bloco `reuniaoInicial` continua aceito pelo backend, mas a tela
   * não o manda mais (revisão de 06/09): o agendamento acontece em outro ponto da
   * jornada (Agenda / detalhe da reserva), não no cadastro.
   */
  /** `GET /{id}/respostas-elegibilidade` — o que o consultor respondeu, com a avaliação manual. */
  respostasElegibilidade: async (id: string): Promise<RespostaElegibilidadeReserva[]> => {
    return (await api.get<RespostaElegibilidadeReserva[]>(
      `${BASE_URL}/${id}/respostas-elegibilidade`
    )) as unknown as RespostaElegibilidadeReserva[]
  },

  /**
   * `PUT /{id}/respostas-elegibilidade` — substitui as respostas às perguntas cadastrais do
   * produto (etapa de documentos do cadastro, 11/09). Só reserva em RASCUNHO.
   */
  substituirRespostasElegibilidade: async (
    id: string,
    respostas: RespostaElegibilidadeRequest[]
  ): Promise<RespostaElegibilidadeReserva[]> => {
    return (await api.put<RespostaElegibilidadeReserva[]>(
      `${BASE_URL}/${id}/respostas-elegibilidade`,
      { respostas }
    )) as unknown as RespostaElegibilidadeReserva[]
  },

  /** `PUT /{id}/respostas-elegibilidade/{perguntaId}/avaliacao` — OPERADOR ou acima. */
  avaliarRespostaElegibilidade: async (
    id: string,
    perguntaId: string,
    request: AvaliacaoRespostaRequest
  ): Promise<RespostaElegibilidadeReserva> => {
    return (await api.put<RespostaElegibilidadeReserva>(
      `${BASE_URL}/${id}/respostas-elegibilidade/${perguntaId}/avaliacao`,
      request
    )) as unknown as RespostaElegibilidadeReserva
  },

  /**
   * P5 (AN-PP5 §3.6, §9): reservas que podem ser a "operação anterior" de uma Nova Reserva —
   * rota dedicada do backend (PP5-O1): mesmo CNPJ e mesmo produto, ATIVO/ENCERRADA, no escopo de
   * quem pede (consultor: a própria carteira — `consultorId` é ignorado para ele; back-office no
   * "em nome de": a carteira do consultor escolhido). Nada é filtrado aqui. A validação final
   * (dono, tenant, produto, status) é do `POST` (422 `OPERACAO_ANTERIOR_INVALIDA`).
   */
  operacoesAnteriores: async (
    cnpj: string,
    produtoId: string,
    consultorId?: string | null
  ): Promise<OperacaoAnteriorCandidata[]> => {
    const params = new URLSearchParams({ cnpj, produtoId })
    if (consultorId) params.set('consultorId', consultorId)
    return (await api.get<OperacaoAnteriorCandidata[]>(
      `${BASE_URL}/operacoes-anteriores?${params.toString()}`
    )) as unknown as OperacaoAnteriorCandidata[]
  },

  criarReserva: async (request: CriarOportunidadeRequest): Promise<Oportunidade> => {
    const response = await api.post<Oportunidade>(BASE_URL, request)
    return response as unknown as Oportunidade
  },

  atualizar: async (id: string, request: OportunidadeUpdateRequest): Promise<Oportunidade> => {
    const response = await api.put<Oportunidade>(`${BASE_URL}/${id}`, request)
    return response as unknown as Oportunidade
  },

  // `atualizarEstagio` (PUT/PATCH /{id}/estagio) saiu em 28/09: a "porta lateral" que
  // gravava o estágio legado sem máquina de status, snapshot nem evento foi removida do
  // backend (Rodada 1, DEV-1). Toda mudança de etapa passa por `transicionarStatus`.

  deletar: async (id: string): Promise<void> => {
    await api.delete(`${BASE_URL}/${id}`)
  },

  /**
   * Transição na máquina de 18 status. O backend valida a transição — a UI só
   * evita oferecer movimentos que ele recusaria. Desde 28/09 (V47) o corpo leva
   * `motivoPerdaCodigo` (obrigatório para CANCELADA/NAO_CONVERTIDA) e `versaoEsperada`
   * (409 `CONFLITO_VERSAO` se a reserva mudou desde a leitura).
   */
  transicionarStatus: async (
    id: string,
    request: AtualizarStatusRequest
  ): Promise<Oportunidade> => {
    const response = await api.patch<Oportunidade>(`${BASE_URL}/${id}/status`, request)
    return response as unknown as Oportunidade
  },

  /**
   * Histórico real de etapas (V47): um snapshot por transição — status/etapa/score
   * anterior e novo, versão da tabela, motivo de perda quando houver, autor e data.
   * Ordem cronológica, como o backend devolve. Escopo é do backend (dono/tenant).
   */
  historicoEtapas: async (id: string): Promise<HistoricoEtapa[]> => {
    const response = await api.get<HistoricoEtapa[]>(`${BASE_URL}/${id}/historico-etapas`)
    return (response as unknown as HistoricoEtapa[]) ?? []
  },

  /**
   * Back-office pede ao consultor a reunião com o cliente (entrega G). OPERADOR+ — o backend
   * recusa o consultor com 403. Avisa o consultor (notificação) e destaca a reserva em
   * Minhas Reservas; pode ser repetido (reenvia o aviso).
   */
  solicitarReuniao: async (
    id: string,
    request: SolicitarReuniaoRequest = {}
  ): Promise<Oportunidade> => {
    const response = await api.post<Oportunidade>(`${BASE_URL}/${id}/solicitacao-reuniao`, request)
    return response as unknown as Oportunidade
  },

  /** Desfaz a solicitação de reunião (não notifica). 400 `REUNIAO_NAO_SOLICITADA` se não houver. */
  cancelarSolicitacaoReuniao: async (id: string): Promise<Oportunidade> => {
    const response = await api.delete<Oportunidade>(`${BASE_URL}/${id}/solicitacao-reuniao`)
    return response as unknown as Oportunidade
  },

  /**
   * Grava a "Análise da Originei" (entrega H): texto livre, uso interno — o consultor
   * nunca recebe. Vazio/nulo limpa. Vai como contexto para a IA na Defesa de crédito.
   */
  salvarAnaliseOriginei: async (id: string, analiseOriginei: string | null): Promise<Oportunidade> => {
    const response = await api.patch<Oportunidade>(`${BASE_URL}/${id}/analise-originei`, { analiseOriginei })
    return response as unknown as Oportunidade
  },

  /**
   * Estende o prazo da reserva e rearma o alerta de vencimento.
   * Só ADMIN/GERENTE/OPERADOR — o backend rejeita o consultor com 403.
   */
  renovar: async (id: string, request: RenovarReservaRequest): Promise<Oportunidade> => {
    const response = await api.post<Oportunidade>(`${BASE_URL}/${id}/renovar`, request)
    return response as unknown as Oportunidade
  },

  /**
   * Registra o parceiro escolhido pela Originei (seleção manual, sem ranking — Handoff
   * v4.0). ADMIN — o backend rejeita os demais com 403. O consultor passa a ver somente o
   * parceiro selecionado.
   */
  selecionarParceiro: async (
    id: string,
    request: { parceiroId: string; parceiroNome: string; motivo?: string; usuarioId: string }
  ): Promise<Oportunidade> => {
    const response = await api.put<Oportunidade>(`${BASE_URL}/${id}/parceiro-selecionado`, request)
    return response as unknown as Oportunidade
  },

  /**
   * Define ou limpa a "próxima ação" da reserva (Fase 6, item 18). Consultor na própria
   * carteira, back-office em qualquer uma. Texto vazio limpa.
   */
  definirProximaAcao: async (id: string, proximaAcao: string): Promise<Oportunidade> => {
    const response = await api.put<Oportunidade>(`${BASE_URL}/${id}/proxima-acao`, { proximaAcao })
    return response as unknown as Oportunidade
  },

  /**
   * Encaminha formalmente a reserva ao parceiro selecionado (Épico 4). ADMIN — o backend
   * rejeita os demais com 403; exige parceiro selecionado e bloqueia se já houver um
   * encaminhamento aguardando retorno. Gera o pacote imutável (hash + protocolo) e
   * publica o evento via outbox.
   */
  encaminharParceiro: async (id: string): Promise<EncaminhamentoParceiro> => {
    const response = await api.post<EncaminhamentoParceiro>(`${BASE_URL}/${id}/encaminhamentos`, {})
    return response as unknown as EncaminhamentoParceiro
  },

  /** Histórico de encaminhamentos da reserva, versão mais recente primeiro. OPERADOR+. */
  listarEncaminhamentos: async (id: string): Promise<EncaminhamentoParceiro[]> => {
    const response = await api.get<EncaminhamentoParceiro[]>(`${BASE_URL}/${id}/encaminhamentos`)
    return response as unknown as EncaminhamentoParceiro[]
  },

  /**
   * Move a reserva para a carteira de outro consultor. ADMIN/GERENTE — o backend
   * rejeita consultor e operador com 403.
   */
  transferir: async (
    id: string,
    request: TransferirReservaRequest
  ): Promise<TransferenciaReserva> => {
    const response = await api.post<TransferenciaReserva>(`${BASE_URL}/${id}/transferir`, request)
    return response as unknown as TransferenciaReserva
  },

  /**
   * Checklist de documentos obrigatórios da reserva — MESMA regra que bloqueia o
   * envio no backend. A tela não mantém cópia do catálogo.
   */
  documentosObrigatorios: async (id: string): Promise<DocumentoObrigatorio[]> => {
    const response = await api.get<DocumentoObrigatorio[]>(
      `${BASE_URL}/${id}/documentos-obrigatorios`
    )
    return (response as unknown as DocumentoObrigatorio[]) ?? []
  },

  /**
   * Catálogo produto → documentos exigidos. O wizard de criação precisa da regra
   * antes de a reserva existir, então não pode usar o checklist por reserva.
   */
  /**
   * Catálogo completo. As chaves são `produtoId` para produtos do catálogo e, para as
   * reservas anteriores ao vínculo formal, o NOME antigo do produto.
   */
  documentosObrigatoriosCatalogo: async (): Promise<Record<string, DocumentoObrigatorio[]>> => {
    const response = await api.get<Record<string, DocumentoObrigatorio[]>>(
      `${BASE_URL}/documentos-obrigatorios/catalogo`
    )
    return (response as unknown as Record<string, DocumentoObrigatorio[]>) ?? {}
  },

  /** Score de aderência — calculado na leitura; a resposta carrega a decomposição. */
  scoreAderencia: async (id: string): Promise<import('@/types/oportunidade').ScoreAderencia> => {
    return await api.get<import('@/types/oportunidade').ScoreAderencia>(
      `${BASE_URL}/${id}/score-aderencia`
    )
  },

  /** Checklist documental de um produto. Mesma regra que bloqueia RASCUNHO → ENVIADA. */
  exigenciasDoProduto: async (produtoId: string): Promise<DocumentoObrigatorio[]> => {
    return await api.get<DocumentoObrigatorio[]>(
      `${BASE_URL}/documentos-obrigatorios/produto/${produtoId}`
    )
  },

  /**
   * Substitui a lista inteira — se a tela salva um checklist, é o checklist que vale.
   *
   * Por isso `categoria` viaja em todo item: o PUT apaga e recria, e item que chega sem
   * categoria é gravado como CADASTRAL. Omitir o campo aqui não "mantém o que estava" —
   * rebaixa o agrupamento inteiro do produto.
   */
  definirExigenciasDoProduto: async (
    produtoId: string,
    itens: {
      rotulo: string
      tipo: string
      ordem?: number
      categoria?: import('@/types/estruturacao').CategoriaDocumento
      /** Devolvida como veio do backend: renomear o rotulo nao pode trocar a chave. */
      chave?: string | null
      obrigatorio?: boolean
      basico?: boolean
    }[]
  ): Promise<DocumentoObrigatorio[]> => {
    return await api.put<DocumentoObrigatorio[]>(
      `${BASE_URL}/documentos-obrigatorios/produto/${produtoId}`,
      itens
    )
  },

  /** Histórico de carteira da reserva (mais recente primeiro). */
  listarTransferencias: async (id: string): Promise<TransferenciaReserva[]> => {
    const response = await api.get<TransferenciaReserva[]>(`${BASE_URL}/${id}/transferencias`)
    return (response as unknown as TransferenciaReserva[]) ?? []
  },

  atualizarDefesaCredito: async (
    id: string,
    request: DefesaCreditoRequest
  ): Promise<Oportunidade> => {
    const response = await api.patch<Oportunidade>(
      `${BASE_URL}/${id}/defesa-credito`,
      request
    )
    return response as unknown as Oportunidade
  },

  gerarPdfDefesaCredito: async (
    id: string,
    context: {
      cedente?: {
        razaoSocial?: string | null
        cnpj?: string | null
        dataConstituicao?: string | null
        atividadePrincipal?: string | null
        endereco?: string | null
      }
      sacado?: { razaoSocial?: string | null; cnpj?: string | null }
    } = {}
  ): Promise<Blob> => {
    const response = await api.post(`${BASE_URL}/${id}/defesa-credito/pdf`, context, {
      responseType: 'blob',
    })
    return response as unknown as Blob
  },

  listarPorCliente: async (clienteId: string): Promise<Oportunidade[]> => {
    const response = await api.get<Oportunidade[]>(`${BASE_URL}/cliente/${clienteId}`)
    return response as unknown as Oportunidade[]
  },

  filtrar: async (
    page: number = 0,
    size: number = 10,
    filtros: Record<string, string | number>
  ): Promise<PageResponse<Oportunidade>> => {
    const params = new URLSearchParams()
    params.append('page', String(page))
    params.append('size', String(size))

    Object.entries(filtros).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, String(value))
      }
    })

    const response = await api.get<PageResponse<Oportunidade>>(
      `${BASE_URL}?${params.toString()}`
    )
    return response as unknown as PageResponse<Oportunidade>
  },
}
